import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Zone } from './zone';
import { withSiteMeta } from '../../HOCS/withSiteMeta';
import { Media } from '../presentational/media';
import { useAdblockDetection } from '../adblock/useAdblockDetection';
import { useDebounce } from '../../scripts/hooks/useDebounce';
import { useIsClient } from '../../scripts/hooks/useIsClient';
import { useMedia } from '../grid/hooks';

type TInContentAdProps = {
  siteConfig: {
    displayAdsProvider?: string;
  };
  at?: any; // @artsy/fresnel media type
};

export function InContentAdBase(props: TInContentAdProps) {
  const location = useLocation();
  const debouncedLocation = useDebounce(location, 250);
  const isClient = useIsClient();
  const { isBlocked: adsBlocked } = useAdblockDetection();
  const isMobile = useMedia(['(max-width: 768px)'], [true], false);
  const displayAdsProvider = props.siteConfig.displayAdsProvider;

  // Playwire ramp lifecycle: register in_content unit on mount/navigation
  useEffect(() => {
    if (displayAdsProvider !== 'playwire' || adsBlocked) {
      return;
    }
    const { ramp } = window as any;
    if (ramp) {
      const actions = () => {
        ramp.addUnits({ type: 'in_content' }).then(() => {
          ramp.displayUnits();
        });
      };

      if (ramp.initCallbackCalled) {
        actions();
      } else {
        const oldCallback = ramp.initCallback;
        ramp.initCallback = () => {
          if (oldCallback) {
            oldCallback();
          }
          actions();
        };
      }
    }

    // do not delete the in_content unit
  }, [debouncedLocation, isClient, displayAdsProvider, adsBlocked]);

  // Show the standard leaderboard blocker for in-content desktop ads.
  if (adsBlocked) {
    if (isMobile) {
      return null;
    }

    const blocker = (
      <div className="slds-text-align--center slds-m-vertical--large">
        <Zone type="horizontal_inlist" />
      </div>
    );

    if (props.at) {
      return <Media at={props.at}>{blocker}</Media>;
    }

    return blocker;
  }

  let inContentComponent = null;
  if (displayAdsProvider === 'playwire') {
    if (!isClient) {
      return null;
    }
    inContentComponent = (
      <div style={{ margin: '20px 0' }}>
        <div className="pw-desktop_in_article" />
      </div>
    );
  } else if (displayAdsProvider === 'nitropay') {
    if (!isClient) {
      return null;
    }
    inContentComponent = (
      <div style={{ margin: '20px 0' }} className="slds-text-align--center">
        <Zone type={isMobile ? 'nitro_in_content_mobile' : 'nitro_in_content_desktop'} />
      </div>
    );
  } else if (displayAdsProvider === 'mediavine') {
    inContentComponent = (
      <div className="mv_slot_target" data-slot="content" />
    );
  } else if (displayAdsProvider === 'tds') {
    inContentComponent = (
      <div className="slds-text-align--center">
        <Zone type="tds_in_content" />
      </div>
    );
  } else if (displayAdsProvider === 'aditude') {
    inContentComponent = (
      <div className="rio-in-content-wrapper slds-text-align--center slds-p-around--medium">
        <Zone type="horizontal_inlist" />
      </div>
    );
  }

  if (inContentComponent) {
    if (props.at) {
      return <Media at={props.at}>{inContentComponent}</Media>;
    }

    return inContentComponent;
  }

  return null;
}

export const InContentAd = withSiteMeta(InContentAdBase);
